/**
 * AppointmentForm
 * Multi-step appointment creation wizard:
 *   1. Template selection
 *   2. Insured person details
 *   3. Examiner + date + time slot
 *   4. Review & submit
 */
import { useState, useMemo } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { format, startOfDay, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  Loader2,
  Clock,
  User,
  Calendar as CalendarIcon,
  BadgeCheck,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Label,
  Skeleton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Calendar,
  Input,
} from "@jourfx/ui";
import { ConflictWarnings } from "@/components/scheduling/conflicts/ConflictWarnings";
import type { Conflict } from "@/components/scheduling/conflicts/ConflictWarnings";
import { useAuth } from "@/hooks/useAuth";
import { VersichertenPicker } from "./VersichertenPicker";

// ─── Types ───────────────────────────────────────────────────────────────────

interface SelectedTemplate {
  _id: string;
  name: string;
  duration: number;
  color: string;
  description?: string;
  requiresAssessment: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function AppointmentForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);

  // Step 1 state
  const [selectedTemplate, setSelectedTemplate] =
    useState<SelectedTemplate | null>(null);

  // Step 2 state
  const [selectedVersicherteId, setSelectedVersicherteId] = useState<string | null>(null);

  // Step 3 state
  const [selectedGutachterId, setSelectedGutachterId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd")
  );
  const [selectedTime, setSelectedTime] = useState<string>("");

  // Options
  const [sendEmailInvite, setSendEmailInvite] = useState(true);
  const [enableReminders, setEnableReminders] = useState(true);

  // Result state
  const [createdAccessCode, setCreatedAccessCode] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // ─── Queries ────────────────────────────────────────────────────────────

  const templates = useQuery(api.templates.index.list);
  const gutachter = useQuery(api.appointments.index.listGutachter);

  const dateOffsetMinutes = useMemo(() => {
    if (!selectedDate) return undefined;
    return parseISO(selectedDate).getTimezoneOffset();
  }, [selectedDate]);

  const selectedScheduledTimestamp = useMemo(() => {
    if (!selectedDate || !selectedTime) return undefined;
    const [hour, minute] = selectedTime.split(":").map(Number);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return undefined;

    const scheduled = new Date(selectedDate);
    scheduled.setHours(hour, minute, 0, 0);
    return scheduled.getTime();
  }, [selectedDate, selectedTime]);

  const conflicts = useQuery(
    api.availability.conflicts.checkConflicts,
    selectedGutachterId &&
    selectedScheduledTimestamp &&
    selectedTemplate &&
    dateOffsetMinutes !== undefined
      ? ({
          gutachterId: selectedGutachterId as never,
          scheduledAt: selectedScheduledTimestamp,
          duration: selectedTemplate.duration,
          tzOffsetMinutes: dateOffsetMinutes,
          minBookingLeadTimeHours: 0,
        } as never)
      : "skip"
  );

  const selectedVersicherter = useQuery(
    api.versichertenProfiles.index.get,
    selectedVersicherteId ? { versichertenProfileId: selectedVersicherteId as any } : "skip"
  );

  const createAppointment = useMutation(api.appointments.index.create);
  const displayConflicts = useMemo<Conflict[]>(
    () =>
      ((conflicts as Conflict[] | undefined) ?? []).map((conflict) =>
        conflict.type === "outside_availability"
          ? { ...conflict, severity: "warning" }
          : conflict
      ),
    [conflicts]
  );
  const hasBlockingConflicts = displayConflicts.some((conflict) => conflict.severity === "error");

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleTemplateSelect = (tpl: SelectedTemplate) => {
    setSelectedTemplate(tpl);
    setStep(2);
  };

  const handleScheduleNext = () => {
    if (!selectedGutachterId || !selectedScheduledTimestamp) return;
    setStep(4);
  };

  const handleSubmit = async () => {
    if (
      !selectedTemplate ||
      !selectedScheduledTimestamp ||
      !selectedGutachterId ||
      !selectedVersicherteId
    ) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createAppointment({
        gutachterId: selectedGutachterId as any,
        templateId: selectedTemplate._id as any,
        versichertenProfileId: selectedVersicherteId as any,
        scheduledAt: selectedScheduledTimestamp,
        duration: selectedTemplate.duration,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        sendEmailInvite,
        enableReminders,
      });

      if (result?.accessCode) {
        setCreatedAccessCode(result.accessCode);
        setStep(5);
      } else {
        navigate({ to: "/appointments" });
      }
    } catch (err) {
      console.error("Failed to create appointment:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Step indicators ───────────────────────────────────────────────────

  const steps = [
    { num: 1, label: "Vorlage", icon: CalendarIcon },
    { num: 2, label: "Versicherter", icon: User },
    { num: 3, label: "Termin", icon: Clock },
    { num: 4, label: "Prüfen", icon: Check },
  ];

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Step indicator */}
      {step <= 4 && (
        <nav className="flex items-center justify-center gap-2" data-testid="step-indicator">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isActive = step === s.num;
            const isDone = step > s.num;
            return (
              <div key={s.num} className="flex items-center gap-2">
                {i > 0 && (
                  <div className={`h-px w-8 ${isDone ? "bg-primary" : "bg-border"}`} />
                )}
                <div
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isDone
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
              </div>
            );
          })}
        </nav>
      )}

      {/* Step 1 — Template selection */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Terminvorlage wählen</CardTitle>
            <CardDescription>Wählen Sie die Art des Termins aus.</CardDescription>
          </CardHeader>
          <CardContent>
            {user?.organization && (
              <div className="mb-4 rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                Geschäftsbereich: <span className="font-medium text-foreground">{user.organization.name}</span>
              </div>
            )}
            {templates === undefined ? (
              <div className="grid gap-3 sm:grid-cols-2" data-testid="loading-skeleton">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-24 rounded-lg" />
                ))}
              </div>
            ) : templates.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                Keine Vorlagen verfügbar. Bitte kontaktieren Sie den Administrator.
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {templates.map((tpl: any) => (
                  <button
                    key={tpl._id}
                    onClick={() =>
                      handleTemplateSelect({
                        _id: tpl._id as string,
                        name: tpl.name,
                        duration: tpl.duration,
                        color: tpl.color,
                        description: tpl.description,
                        requiresAssessment: tpl.requiresAssessment,
                      })
                    }
                    className="flex flex-col gap-1 rounded-lg border p-4 text-left transition-colors hover:bg-accent"
                    data-testid="template-card"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: tpl.color }} />
                      <span className="font-medium">{tpl.name}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">{tpl.duration} Minuten</span>
                    {tpl.description && (
                      <span className="text-xs text-muted-foreground">{tpl.description}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2 — Versicherten selection */}
      {step === 2 && (
        <VersichertenPicker
          selectedVersicherteId={selectedVersicherteId}
          onSelect={setSelectedVersicherteId}
          onBack={() => setStep(1)}
          onNext={() => { if (selectedVersicherteId) setStep(3); }}
        />
      )}

      {/* Step 3 — Examiner, date, time slot */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Termin festlegen</CardTitle>
            <CardDescription>Wählen Sie Gutachter, Datum und Uhrzeit.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Gutachter select */}
            <div className="space-y-2">
              <Label>Gutachter *</Label>
              {gutachter === undefined ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <select
                  value={selectedGutachterId}
                  onChange={(e) => {
                    setSelectedGutachterId(e.target.value);
                  }}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Gutachter auswählen...</option>
                  {gutachter.map((g: any) => (
                    <option key={g._id} value={g._id as string}>
                      {g.name} ({g.email})
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Date picker */}
            <div className="space-y-2">
              <Label>Datum *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate
                      ? format(parseISO(selectedDate), "dd.MM.yyyy", { locale: de })
                      : "Datum wählen..."}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate ? parseISO(selectedDate) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        setSelectedDate(format(date, "yyyy-MM-dd"));
                      }
                    }}
                    disabled={(date) => date < startOfDay(new Date())}
                    locale={de}
                    defaultMonth={selectedDate ? parseISO(selectedDate) : new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="appointment-time">Uhrzeit *</Label>
              <Input
                id="appointment-time"
                type="time"
                step="60"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Beliebige Uhrzeit im Format HH:mm.
              </p>
            </div>

            {/* Conflict warnings */}
            {displayConflicts.length > 0 && (
              <ConflictWarnings conflicts={displayConflicts} />
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Zurück
              </Button>
              <Button
                onClick={handleScheduleNext}
                disabled={!selectedGutachterId || !selectedScheduledTimestamp}
              >
                Weiter
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — Review */}
      {step === 4 && selectedTemplate && selectedScheduledTimestamp && (
        <Card>
          <CardHeader>
            <CardTitle>Zusammenfassung prüfen</CardTitle>
            <CardDescription>Bitte überprüfen Sie alle Angaben vor dem Erstellen.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ReviewRow label="Vorlage" value={selectedTemplate.name} />
            <ReviewRow label="Dauer" value={`${selectedTemplate.duration} Minuten`} />
            <ReviewRow label="Anrede" value={selectedVersicherter?.anrede || "—"} />
            <ReviewRow label="Name" value={selectedVersicherter ? `${selectedVersicherter.firstName} ${selectedVersicherter.lastName}` : "—"} />
            <ReviewRow label="E-Mail" value={selectedVersicherter?.email || "—"} />
            {selectedVersicherter?.phone && <ReviewRow label="Telefon" value={selectedVersicherter.phone} />}
            {selectedVersicherter?.versichertennummer && <ReviewRow label="Versichertennummer" value={selectedVersicherter.versichertennummer} />}
            <ReviewRow
              label="Gutachter"
              value={
                gutachter?.find((g: any) => (g._id as string) === selectedGutachterId)?.name ?? selectedGutachterId
              }
            />
            <ReviewRow
              label="Datum & Uhrzeit"
              value={`${format(parseISO(selectedDate), "dd.MM.yyyy", { locale: de })}, ${selectedTime} Uhr`}
            />

            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <BadgeCheck className="h-4 w-4" />
                Optionen
              </div>
              <div className="mt-2 space-y-2 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sendEmailInvite}
                    onChange={(e) => setSendEmailInvite(e.target.checked)}
                  />
                  Einladung per E-Mail senden
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={enableReminders}
                    onChange={(e) => setEnableReminders(e.target.checked)}
                  />
                  Automatische Erinnerungen aktivieren
                </label>
              </div>
            </div>

            {/* Show error conflicts as blocking */}
            {displayConflicts.length > 0 && (
              <ConflictWarnings conflicts={displayConflicts} />
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(3)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Zurück
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  isSubmitting || hasBlockingConflicts
                }
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Termin erstellen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5 — access code result */}
      {step === 5 && createdAccessCode && (
        <Card>
          <CardContent className="flex flex-col items-center py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-900 mb-4">
              <Check className="h-7 w-7 text-green-600 dark:text-green-300" />
            </div>
            <h2 className="text-xl font-bold">Termin erstellt</h2>
            <p className="mt-2 text-muted-foreground">
              Der Termin wurde erfolgreich erstellt. Teilen Sie den Zugangscode mit dem Versicherten.
            </p>

            <div className="mt-6 w-full max-w-xs">
              <p className="text-sm text-muted-foreground mb-1">Zugangscode</p>
              <div className="flex items-center gap-3 rounded-lg border bg-muted/50 px-6 py-4 justify-center">
                <span className="font-mono text-2xl font-bold tracking-widest">{createdAccessCode}</span>
                <Button variant="ghost" size="icon" onClick={async () => {
                  await navigator.clipboard.writeText(createdAccessCode);
                  setCodeCopied(true);
                  setTimeout(() => setCodeCopied(false), 2000);
                }}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {codeCopied && (
                <p className="mt-1 text-sm text-green-600">
                  Zugangscode in Zwischenablage kopiert
                </p>
              )}
            </div>

            <div className="mt-8 flex gap-3">
              <Button variant="outline" asChild>
                <Link to="/appointments">Zur Terminliste</Link>
              </Button>
              <Button
                onClick={() => {
                  setStep(1);
                  setSelectedTemplate(null);
                  setSelectedVersicherteId(null);
                  setSelectedGutachterId("");
                  setSelectedTime("");
                  setCreatedAccessCode(null);
                }}
              >
                Weiteren Termin erstellen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b pb-2 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
